import {
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { CoachMessage } from '../../services/coaching.service';
import { isSameDay, isTouchDevice } from '../../services/chat-utils';

/** A message typed here that the server hasn't confirmed yet. */
export interface PendingChatMessage {
  tempId: string;
  content: string;
  created_on: string;
  status: 'PENDING' | 'FAILED';
  error: string | null;
}

const NEAR_BOTTOM_PX = 96;
let tempSeq = 0;

/**
 * Message list + composer shared by the coach inbox and the user messenger.
 * Owns scroll anchoring ("N new" pill), "Load earlier", optimistic sends with
 * retry, and composer focus. Hosts own fetching and the message array.
 */
@Component({
  selector: 'app-coaching-chat-thread',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './coaching-chat-thread.component.html',
  styleUrls: ['./coaching-chat-thread.component.scss']
})
export class CoachingChatThreadComponent implements OnChanges, AfterViewChecked, OnDestroy {
  @Input() threadId: string | null = null;
  @Input() messages: CoachMessage[] = [];
  @Input() loading = false;
  @Input() hasMore = false;
  @Input() loadingOlder = false;
  @Input() canReply = true;
  /** False while the host shows its own composer (e.g. WhatsApp templates). */
  @Input() showComposer = true;
  @Input() placeholder = 'Write a message…';
  @Input() emptyText = 'No messages yet.';
  /** Accessible name of the message log, e.g. "Conversation with Sam". */
  @Input() label = 'Messages';
  /** Show WhatsApp channel icons and delivery ticks. */
  @Input() showDelivery = false;
  @Input() isOutgoing: (m: CoachMessage) => boolean = m => m.from_user === false;
  /** Sender name shown above incoming bubbles (e.g. another coach), or null. */
  @Input() senderLabel: (m: CoachMessage) => string | null = () => null;
  /** Does the actual send; the component handles the optimistic bubble and retry. */
  @Input() sendFn?: (content: string) => Observable<CoachMessage>;
  @Input() autofocus = true;

  /** Server-confirmed message; the host appends it (dedupe by id). */
  @Output() sent = new EventEmitter<CoachMessage>();
  @Output() sendFailed = new EventEmitter<any>();
  @Output() loadOlder = new EventEmitter<void>();

  @ViewChild('messageList') messageList?: ElementRef<HTMLDivElement>;
  @ViewChild('composerInput') composerInput?: ElementRef<HTMLTextAreaElement>;

  draft = '';
  pending: PendingChatMessage[] = [];
  newCount = 0;

  private nearBottom = true;
  private scrollAction: 'bottom' | { height: number; top: number } | null = null;
  private focusOnLoad = false;
  private readonly sends = new Map<string, Subscription>();

  readonly isSameDay = isSameDay;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['threadId']) {
      this.resetForThread();
    } else if (changes['messages']) {
      this.onMessagesChanged(changes['messages'].previousValue || [], this.messages || []);
    }
    if (this.focusOnLoad && !this.loading && this.canReply && this.showComposer) {
      this.focusOnLoad = false;
      setTimeout(() => this.focusComposer());
    }
  }

  ngAfterViewChecked(): void {
    const el = this.messageList?.nativeElement;
    if (!el || !this.scrollAction) {
      return;
    }
    if (this.scrollAction === 'bottom') {
      el.scrollTop = el.scrollHeight;
      this.nearBottom = true;
      this.newCount = 0;
    } else {
      // Older messages were prepended: keep the same message under the viewport.
      el.scrollTop = el.scrollHeight - this.scrollAction.height + this.scrollAction.top;
    }
    this.scrollAction = null;
  }

  ngOnDestroy(): void {
    this.sends.forEach(s => s.unsubscribe());
  }

  focusComposer(): void {
    this.composerInput?.nativeElement.focus({ preventScroll: true });
  }

  onScroll(): void {
    const el = this.messageList?.nativeElement;
    if (!el) {
      return;
    }
    this.nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
    if (this.nearBottom) {
      this.newCount = 0;
    }
  }

  jumpToLatest(): void {
    this.scrollAction = 'bottom';
    this.newCount = 0;
  }

  requestOlder(): void {
    this.loadOlder.emit();
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' || event.shiftKey) {
      return;
    }
    // IME composition (e.g. Japanese, Chinese) uses Enter to confirm a candidate,
    // and on phones Enter should insert a newline — the Send button sends.
    if (event.isComposing || event.keyCode === 229 || isTouchDevice()) {
      return;
    }
    event.preventDefault();
    this.submit();
  }

  submit(): void {
    const content = this.draft.trim();
    if (!content || !this.canReply || !this.sendFn) {
      return;
    }
    const item: PendingChatMessage = {
      tempId: `tmp-${Date.now()}-${++tempSeq}`,
      content,
      created_on: new Date().toISOString(),
      status: 'PENDING',
      error: null
    };
    this.pending = [...this.pending, item];
    this.draft = '';
    this.scrollAction = 'bottom';
    this.dispatch(item);
    this.focusComposer();
  }

  retry(item: PendingChatMessage): void {
    item.status = 'PENDING';
    item.error = null;
    this.dispatch(item);
  }

  discard(item: PendingChatMessage): void {
    this.sends.get(item.tempId)?.unsubscribe();
    this.sends.delete(item.tempId);
    this.pending = this.pending.filter(p => p !== item);
  }

  statusIcon(m: CoachMessage): string {
    switch (m.status) {
      case 'READ': return 'bi-check2-all read';
      case 'DELIVERED': return 'bi-check2-all';
      case 'FAILED': return 'bi-exclamation-circle failed';
      case 'PENDING': return 'bi-clock';
      default: return 'bi-check2';
    }
  }

  trackById(_: number, m: CoachMessage): string {
    return m.id;
  }

  trackPending(_: number, p: PendingChatMessage): string {
    return p.tempId;
  }

  private dispatch(item: PendingChatMessage): void {
    const threadId = this.threadId;
    this.sends.get(item.tempId)?.unsubscribe();
    const sub = this.sendFn!(item.content).subscribe({
      next: message => {
        this.sends.delete(item.tempId);
        if (this.threadId === threadId) {
          this.pending = this.pending.filter(p => p !== item);
          this.scrollAction = 'bottom';
        }
        this.sent.emit(message);
      },
      error: err => {
        this.sends.delete(item.tempId);
        item.status = 'FAILED';
        item.error = err?.error?.message || 'Not sent';
        this.pending = [...this.pending];
        this.sendFailed.emit(err);
      }
    });
    this.sends.set(item.tempId, sub);
  }

  private resetForThread(): void {
    this.sends.forEach(s => s.unsubscribe());
    this.sends.clear();
    this.pending = [];
    this.draft = '';
    this.newCount = 0;
    this.nearBottom = true;
    this.scrollAction = 'bottom';
    this.focusOnLoad = this.autofocus;
  }

  private onMessagesChanged(prev: CoachMessage[], next: CoachMessage[]): void {
    if (!prev.length || !next.length) {
      this.scrollAction = 'bottom';
      return;
    }
    const prevFirst = prev[0]?.id;
    const prevLast = prev[prev.length - 1]?.id;
    const nextLast = next[next.length - 1]?.id;

    if (next[0]?.id !== prevFirst && nextLast === prevLast) {
      const el = this.messageList?.nativeElement;
      if (el) {
        this.scrollAction = { height: el.scrollHeight, top: el.scrollTop };
      }
      return;
    }
    if (nextLast === prevLast) {
      return;
    }

    const lastIndex = next.findIndex(m => m.id === prevLast);
    const appended = lastIndex >= 0 ? next.slice(lastIndex + 1) : next.filter(m => !prev.some(p => p.id === m.id));
    this.reconcilePending(appended);

    const incoming = appended.filter(m => !this.isOutgoing(m));
    if (this.nearBottom || incoming.length < appended.length) {
      this.scrollAction = 'bottom';
    } else {
      this.newCount += incoming.length;
    }
  }

  /** The socket echo can beat the HTTP response: drop the optimistic copy once the real one shows. */
  private reconcilePending(appended: CoachMessage[]): void {
    if (!this.pending.length) {
      return;
    }
    const claimed = new Set<string>();
    this.pending = this.pending.filter(p => {
      if (p.status !== 'PENDING') {
        return true;
      }
      const match = appended.find(m => !claimed.has(m.id) && this.isOutgoing(m) && (m.content || '').trim() === p.content);
      if (!match) {
        return true;
      }
      claimed.add(match.id);
      return false;
    });
  }
}
